import classNames from "classnames";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import useIsAvailabilityPermitted from "PFApp/use_is_availability_permitted";
import BookingsCalendar from "PFComponents/calendar/bookings_calendar/bookings_calendar";
import { utc } from "PFCore/helpers/date";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useBookingApprovalsInvalidate } from "PFCore/hooks/queries/bookings/approvals/use_booking_approvals_invalidate";
import {
  CalendarGranularity,
  useProfilesAvailabilities
} from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import useAvailabilityLimits from "PFCore/hooks/use_availability_limits";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { BookingFormMode } from "../../booking/components/booking_form";
import { useDetailsPanelsActions } from "../../booking/components/details_panel";
import { ReassignActionContextProvider } from "../../booking/parts/overview/booking_module_calendar/context/reassign_action_context_provider";
import css from "./availability.module.scss";
import { getProfilesAvailabilityDateRange } from "./availability.utils";
import { BookingsList } from "./bookings_list";
import { useProfileBookingsContext } from "./profile_bookings_context";
import { ProfileBookingsProvider } from "./profile_bookings_provider";

export const AvailabilityContent = ({
  profile,
  isLoading,
  activeViews,
  isAdmin,
  showTip,
  readOnly = false,
  isExpanded = false
}) => {
  const { t } = useTranslation("availability");
  const {
    selectedBooking,
    selectBooking,
    bookings,
    refreshBookings,
    handleDateRangeChange,
    jobCodeDisplayAs,
    calendarSearch
  } = useProfileBookingsContext();
  const { openDetailsPanel, closeTopDetailsPanel, openBookingFormSidePanel } = useDetailsPanelsActions();

  const { isEnabled: isBookingModuleEnabled } = useBookingModulePermissions();
  const canEditAvailability = useIsAvailabilityPermitted(profile, ["rw"]) && !readOnly;

  const { invalidate: invalidateBookingApprovals } = useBookingApprovalsInvalidate();
  const { minDate, maxDate } = useAvailabilityLimits();

  const canEdit = !readOnly && canEditAvailability;

  const { getCategory, bookingCategories } = useBookingCategories();

  const [displayMonth, setDisplayMonth] = useState(utc());

  const { minDisplayedDate, maxDisplayedDate } = getProfilesAvailabilityDateRange(displayMonth);

  const { availabilitiesByProfile } = useProfilesAvailabilities({
    profiles: [{ id: profile.id, rowKey: `${profile.id}` }],
    dateRange: {
      min: minDisplayedDate,
      max: maxDisplayedDate
    },
    calendarGranularity: CalendarGranularity.Daily
  });
  const profileAvailabilities = availabilitiesByProfile[profile.id];

  const handlePeriodClick = (period) => {
    selectBooking(period);
    setDisplayMonth(utc(period.start_date));
  };

  const isListView = activeViews?.includes("list");
  const isCalendarView = activeViews?.includes("calendar");

  const checkCanEditBooking = (booking) =>
    canEdit && (isBookingModuleEnabled || (!booking.activity_id && booking.source === "ui"));

  const handleBookingsUpdate = () => {
    invalidateBookingApprovals();
    refreshBookings();
  };

  const handleEdit = ({ id }) => {
    const isSuspended = !!profile.suspendedAt;
    const suspendedProfileDetails = isSuspended
      ? {
          id: profile.id,
          firstName: profile.firstName,
          lastName: profile.lastName,
          status: "inactive",
          suspendedAt: profile.suspendedAt
        }
      : undefined;
    openBookingFormSidePanel(
      {
        bookingId: id,
        ...(isSuspended ? { suspendedProfileDetails } : {})
      },
      { mode: BookingFormMode.EditSingle, onSuccess: handleBookingsUpdate }
    );
  };

  useEffect(() => {
    if (!isBookingModuleEnabled) {
      return;
    }
    if (!selectedBooking) {
      closeTopDetailsPanel();
      return;
    }
    const isSuspended = !!profile.suspendedAt;
    const suspendedProfileDetails = isSuspended
      ? {
          id: profile.id,
          firstName: profile.firstName,
          lastName: profile.lastName,
          status: "inactive",
          suspendedAt: profile.suspendedAt
        }
      : undefined;
    openDetailsPanel({
      type: "booking",
      id: selectedBooking.id,
      category: getCategory(selectedBooking),
      isEditPermitted: checkCanEditBooking(selectedBooking),
      ...(isSuspended ? { suspendedProfileDetails } : {})
    });
  }, [selectedBooking]);

  useEffect(() => {
    if (isListView) {
      handleDateRangeChange?.(
        displayMonth.clone().startOf("month"),
        displayMonth.clone().endOf("month"),
        displayMonth
      );
    }
  }, [isListView, displayMonth]);

  const tip =
    showTip &&
    (!canEdit ? t("tip.content.readOnly") : isAdmin ? t("tip.content.admin") : t("tip.content.user"));

  return (
    <div className={classNames(css.root, { [css.listView]: isListView })}>
      {tip && (
        <>
          <div style={{ opacity: 0.7 }}>{t("tip.head")}</div>
          <small className={css.tip}>{tip}</small>
        </>
      )}
      <>
        {isCalendarView && (
          <BookingsCalendar
            darkBg
            calendars={profile.availability.calendars}
            bookings={bookings}
            bookingCategories={bookingCategories}
            style={{ maxWidth: "100%" }}
            minDate={minDate}
            maxDate={maxDate}
            onClick={selectBooking}
            displayMonth={displayMonth}
            setDisplayMonth={setDisplayMonth}
            jobCodeDisplayAs={jobCodeDisplayAs}
            onDisplayRangeChange={handleDateRangeChange}
            isExpanded={isExpanded}
            availabilities={profileAvailabilities ?? []}
            calendarSearch={calendarSearch}
          />
        )}
        {isListView && (
          <BookingsList
            checkCanEditBooking={checkCanEditBooking}
            isLoading={isLoading}
            onBookingClick={handlePeriodClick}
            onBookingEdit={handleEdit}
            minDate={minDate}
            maxDate={maxDate}
            displayMonth={displayMonth}
            setDisplayMonth={setDisplayMonth}
            hideMonthSelector={isCalendarView}
            isExpanded={isExpanded}
          />
        )}
      </>
    </div>
  );
};

export const AvailabilityContext = ({ profile, children, isSearchEnabled }) => {
  const {
    data: { templates, availability }
  } = useMyAccountInfo();

  return (
    <ProfileBookingsProvider
      templates={templates}
      profile={profile}
      bookingCategories={availability?.booking_categories}
      isSearchEnabled={isSearchEnabled}
    >
      <ReassignActionContextProvider>{children}</ReassignActionContextProvider>
    </ProfileBookingsProvider>
  );
};

const contextProps = {
  profile: PropTypes.shape({ id: PropTypes.number, availability: PropTypes.object }),
  isAdmin: PropTypes.bool,
  children: PropTypes.node,
  isSearchEnabled: PropTypes.bool
};

const props = {
  isLoading: PropTypes.bool,
  activeViews: PropTypes.arrayOf(PropTypes.string),
  showTip: PropTypes.bool,
  readOnly: PropTypes.bool,
  profile: PropTypes.shape({ id: PropTypes.number, availability: PropTypes.object }),
  isAdmin: PropTypes.bool,
  isExpanded: PropTypes.bool
};

const Availability = ({ isAdmin, profile, ...props }) => (
  <AvailabilityContext profile={profile} isAdmin={isAdmin}>
    <AvailabilityContent isAdmin={isAdmin} profile={profile} {...props} />
  </AvailabilityContext>
);

Availability.propTypes = props;

AvailabilityContent.propTypes = props;

AvailabilityContext.propTypes = contextProps;

export default Availability;
